/**
 * ProtectedRoute - Base authentication guard
 *
 * Ensures user is authenticated before accessing protected routes.
 * Redirects to login page if not authenticated.
 */

import { type ReactNode, useContext } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AuthContext } from "@/contexts/AuthContext";
import { LoadingScreen } from "@/components/ui/LoadingScreen";
import { ROUTES } from "@/app/routes";

interface ProtectedRouteProps {
	children: ReactNode;
}

export const ProtectedRoute = ({ children }: ProtectedRouteProps) => {
	const { t } = useTranslation();
	const authContext = useContext(AuthContext);

	if (!authContext) {
		throw new Error("ProtectedRoute must be used within an AuthProvider");
	}

	const { isAuthenticated, isLoading } = authContext;
	const location = useLocation();

	if (isLoading) {
		return (
			<LoadingScreen
				variant="default"
				size="md"
				message={t("common.verifying_authentication")}
			/>
		);
	}

	if (!isAuthenticated) {
		// Redirect to login page with return URL
		return (
			<Navigate to={ROUTES.AUTH.LOGIN} state={{ from: location }} replace />
		);
	}

	return <>{children}</>;
};
